/**
 * Hook for editing recurring transactions in a modal context.
 *
 * Wraps the existing updateRecurring mutation from useRecurringTransactions
 * and provides additional state management for modal usage.
 *
 * @example
 * ```tsx
 * const { updateRecurring, isUpdating, error } = useRecurringEdit();
 *
 * const handleSave = async (data: RecurringEditFormData) => {
 *   await updateRecurring.mutateAsync({
 *     id: transactionId,
 *     formData: data,
 *   });
 * };
 * ```
 */

import { useMemo } from "react";
import { useRecurringTransactions, type RecurringTransactionFormData } from "./useRecurringTransactions";

export interface UseRecurringEditResult {
  updateRecurring: ReturnType<typeof useRecurringTransactions>['updateRecurring'];
  isUpdating: boolean;
  error: Error | null;
}

export function useRecurringEdit(): UseRecurringEditResult {
  const { updateRecurring: baseUpdateRecurring } = useRecurringTransactions();

  // Expose the mutation directly with all its properties
  const updateRecurring = useMemo(() => baseUpdateRecurring, [baseUpdateRecurring]);

  return {
    updateRecurring,
    isUpdating: updateRecurring.isPending,
    error: updateRecurring.error,
  };
}
